import React from 'react';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type { AIPlugin } from '../../aiPluginType';
import { TelepointerAvatar } from '../../ui/components/TelepointerAvatar/TelepointerAvatar';
import type { StepName } from '../../ui/utils/analytics/analyticsFlowTypes';
import { aiSessionContextPluginKey } from '../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';
import {
	TELEPOINTER_AVATAR_PORTAL_KEY,
	createTelepointerElement,
} from '../in-editor-direct-streaming/decorations';

import { applyPlugin, mapPositions } from './apply';
import { buildStreamingDecorations } from './buildDecorations';
import { createInitialState } from './initialState';
import { purePmInEditorDirectStreamingPluginKey as streamingPluginKey } from './pluginKey';
import {
	buildMarkdownStreamingTransaction,
	isMarkdownStreamingConfig,
} from './transaction-builders/buildMarkdownStreamingTransaction';
import { buildMountTransaction } from './transaction-builders/buildMountTransaction';
import { buildUpdateTransaction } from './transaction-builders/buildUpdateTransaction';
import { endStream } from './transforms';
import type { PurePmInEditorDirectStreamingPluginState } from './types';
import { detectRequiredAction, shouldSkipAppendTransaction } from './utils/sessionDetection';
import { createDiscardViewState, handleDiscard } from './view-update/handleDiscard';
import { restoreEditorState, handleEditorState } from './view-update/handleEditorState';
import { handleExpandNodes } from './view-update/handleExpand';
import { createFinalizeViewState, handleFinalize } from './view-update/handleFinalize';
import {
	cancelLoadingAnimation,
	handleLoadingAnimation,
} from './view-update/handleLoadingAnimation';
import {
	createMarkdownStreamingViewState,
	handleMarkdownStreaming,
} from './view-update/handleMarkdownStreaming';
import { createStreamingViewState } from './view-update/streamingViewState';

/**
 * Creates the in-editor direct streaming plugin for AI content generation.
 *
 * ## Overview
 *
 * This plugin manages the real-time streaming of AI-generated content directly into the editor.
 * It provides visual feedback during streaming (highlights, telepointer cursor) and handles
 * the complete lifecycle from mounting through finalization or discard.
 *
 * ## Architecture
 *
 * The plugin is **reactive** - it observes changes to the `aiSessionContext` plugin state
 * (which holds streaming data from external) and automatically generates transactions
 * to update the document. This decouples the streaming API from document manipulation.
 *
 * ### Key Components:
 *
 * - **State (`apply`)**: Tracks positions, streaming phase, and display flags. Handles
 *   action-based updates via `applyPlugin()` and position remapping when the document changes.
 *
 * - **appendTransaction**: Watches for session state changes and generates appropriate
 *   transactions (`mount`, `update`, `cleanup`). Uses `detectRequiredAction()` to determine
 *   what action is needed based on session/streaming state.
 *
 * - **Decorations**: Renders visual feedback including user content highlight (blue),
 *   AI content highlight (purple), and a telepointer cursor widget.
 *
 * - **View handlers**: Manages side effects that require imperative DOM/view access:
 *   - `handleDiscard`: Restores original content when session is cleared mid-stream
 *   - `handleFinalize`: Commits final content using a two-dispatch pattern for track-changes
 *   - `handleLoadingAnimation`: Animates the loading highlight during initial response wait
 *   - `handleExpandNodes`: Expands collapsed nodes (e.g., expand panels) as content streams
 *   - `handleEditorState`: Manages editor focus, tab index, and outline during streaming
 *
 * ## Streaming Lifecycle
 *
 * ```
 * idle → mounting → streaming → finalizing → idle
 *                 ↘ cleanup/discard → idle
 * ```
 *
 * 1. **Mount**: When a new session starts streaming, initializes positions and decorations
 * 2. **Update**: As chunks arrive, incrementally replaces content and updates positions
 * 3. **Finalize**: On completion, uses two dispatches (restore then commit) for track-changes
 * 4. **Cleanup**: If session changes mid-stream, resets state without restoring content
 * 5. **Discard**: If session is cleared, restores original content and resets state
 *
 * ## Why Two Dispatches for Finalize?
 *
 * The finalize step uses two separate `view.dispatch()` calls rather than `appendTransaction`
 * to ensure track-changes (change tracking) works correctly. The first dispatch restores
 * the original content, the second applies the final AI content. This separation allows
 * track-changes to properly record the change as a single replacement operation.
 */
export const createInEditorDirectStreamingPlugin = (options: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	canAutoAcceptAIChanges: boolean;
	getFireAnalyticsStep?: () => ((step: StepName, targetStep?: StepName) => void) | undefined;
	getIntl: PMPluginFactoryParams['getIntl'];
	portalProviderAPI: PortalProviderAPI;
}): SafePlugin => {
	const { api, canAutoAcceptAIChanges, getFireAnalyticsStep, portalProviderAPI } = options;

	const fireAnalyticsStep: (step: StepName, targetStep?: StepName) => void = (step, targetStep) =>
		getFireAnalyticsStep?.()?.(step, targetStep);

	const intl = options.getIntl();
	const defaultLabel = intl.formatMessage(aiMessages.aiRovoTelepointer);

	const isEditorAgentMentionsTelepointerAvatarEnabled = (): boolean =>
		editorExperiment('platform_editor_agent_mentions', true) &&
		fg('platform_editor_agent_mentions_telepointer_avatar');

	const isEditorAgentMentionsExperimentEnabled = (): boolean =>
		editorExperiment('platform_editor_agent_mentions', true);

	// Create the telepointer element — label is mutated per-session to show agent name
	const {
		element: telepointerElement,
		labelElement: telepointerLabelElement,
		avatarContainer: telepointerAvatarContainer,
	} = createTelepointerElement(defaultLabel);

	// Track rendered agent info to avoid redundant portal re-renders in the decorations() hot path.
	// null = portal was rendered then removed; undefined = never rendered.
	let lastRenderedAgentKey: string | null = null;
	// Track last showAgentAvatar value to avoid redundant style.display DOM writes on every decorations() call
	let lastShowAgentAvatar: boolean | undefined;
	return new SafePlugin({
		key: streamingPluginKey,
		state: {
			init: createInitialState,
			apply(
				tr: ReadonlyTransaction,
				pluginState: PurePmInEditorDirectStreamingPluginState,
			): PurePmInEditorDirectStreamingPluginState {
				const meta = tr.getMeta(streamingPluginKey);

				// Handle action-based state transformations
				// IMPORTANT: Return early when an action is handled because the positions
				// in the action payload are already mapped (e.g., in buildUpdateTransaction).
				// If we also run mapPositions, we'd double-map and get incorrect results.
				if (meta?.action) {
					const newState = applyPlugin(meta, pluginState);
					if (newState !== undefined) {
						return newState;
					}
				}

				// Apply position mapping for transactions that don't have a streaming action
				// but do change the document
				if (tr.docChanged && pluginState.isStreaming) {
					return mapPositions(tr, pluginState);
				}

				return pluginState;
			},
		},

		appendTransaction(
			transactions: readonly Transaction[],
			oldState: EditorState,
			newState: EditorState,
		): Transaction | null {
			const streamingState = streamingPluginKey.getState(newState);
			const sessionState = aiSessionContextPluginKey.getState(newState);
			const prevSessionState = aiSessionContextPluginKey.getState(oldState);
			const decorationState = aiExperienceDecorationPluginKey.getState(newState);

			if (!streamingState || !sessionState) {
				return null;
			}

			// Quick bail-out checks
			if (shouldSkipAppendTransaction(streamingState, sessionState)) {
				return null;
			}

			// Determine what action is needed
			const action = detectRequiredAction(sessionState, prevSessionState, streamingState, false);
			if (!action) {
				return null;
			}

			// Handle markdown streaming separately as it uses a different transaction builder
			// Skip for fallback free-gen (userInput set) — those stream content in-document,
			// not into a title/heading node.
			const isFallbackFreeGen = sessionState.userInput !== undefined;
			const isMarkdownStreaming =
				decorationState?.configItem && isMarkdownStreamingConfig(decorationState.configItem);
			if (
				isMarkdownStreaming &&
				!isFallbackFreeGen &&
				(action === 'update' || action === 'finalize')
			) {
				return buildMarkdownStreamingTransaction(newState, action === 'finalize');
			}

			switch (action) {
				case 'mount':
					return buildMountTransaction(newState, fireAnalyticsStep);

				case 'update':
					return buildUpdateTransaction(newState, fireAnalyticsStep);

				case 'cleanup':
					// Reset streaming state when session changes mid-stream
					return endStream(newState.tr);

				// Finalize & Discard are handled in view.update()
				case 'finalize':
				case 'discard':
				default:
					return null;
			}
		},

		props: {
			decorations(state: EditorState) {
				const decorationState = aiExperienceDecorationPluginKey.getState(state);
				return buildStreamingDecorations(
					state,
					telepointerElement,
					decorationState,
					isEditorAgentMentionsTelepointerAvatarEnabled(),
				);
			},
		},

		view: (editorView: EditorView) => {
			// Mutable state containers for view closure variables
			const viewState = createStreamingViewState();
			const discardState = createDiscardViewState();
			const finalizeState = createFinalizeViewState();
			const markdownStreamingState = createMarkdownStreamingViewState();

			return {
				update(view: EditorView, previousState: EditorState) {
					const state = streamingPluginKey.getState(view.state);
					const prevState = streamingPluginKey.getState(previousState);

					if (!state) {
						return;
					}

					// Hoist a single getState call for use throughout this update() invocation.
					const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
					const { currentSessionId } = decorationState || {};
					const isActive = !!currentSessionId || state.isStreaming || state.isStreamingFinished;

					// Update telepointer avatar display — done here in view.update() rather than
					// in decorations() so that DOM mutations and React portal renders are side-effect-free
					// from the perspective of ProseMirror's decoration rendering pass.
					const agentInfo = decorationState?.agentInfo;
					const agentIdentityAccountId = agentInfo?.agentIdentityAccountId;
					const showAgentAvatar =
						isEditorAgentMentionsTelepointerAvatarEnabled() && !!agentIdentityAccountId;

					if (showAgentAvatar && agentIdentityAccountId) {
						if (lastShowAgentAvatar !== true) {
							lastShowAgentAvatar = true;
							telepointerLabelElement.style.display = 'none';
							telepointerAvatarContainer.style.display = '';
						}
						const agentRenderKey = agentIdentityAccountId;
						if (lastRenderedAgentKey !== agentRenderKey) {
							lastRenderedAgentKey = agentRenderKey;
							const capturedAgentIdentityAccountId = agentRenderKey;
							portalProviderAPI.render(
								() =>
									React.createElement(TelepointerAvatar, {
										agentIdentityAccountId: capturedAgentIdentityAccountId,
									}),
								telepointerAvatarContainer,
								TELEPOINTER_AVATAR_PORTAL_KEY,
							);
						}
					} else {
						if (lastShowAgentAvatar !== false) {
							lastShowAgentAvatar = false;
							telepointerAvatarContainer.style.display = 'none';
							telepointerLabelElement.style.display = '';
						}
						if (lastRenderedAgentKey !== null) {
							lastRenderedAgentKey = null;
							portalProviderAPI.remove(TELEPOINTER_AVATAR_PORTAL_KEY);
						}
						const agentLabel = agentInfo?.agentName;
						const newLabel =
							isEditorAgentMentionsExperimentEnabled() && agentLabel ? agentLabel : defaultLabel;
						if (telepointerLabelElement.textContent !== newLabel) {
							telepointerLabelElement.textContent = newLabel;
						}
					}

					// Handle discard when session is cleared while streaming
					handleDiscard(view, state, discardState, fireAnalyticsStep);

					// Handle finalize with TWO separate dispatches (matching original implementation)
					// This is critical for track-changes to work correctly
					handleFinalize(view, state, finalizeState, fireAnalyticsStep);

					// Handle markdown streaming: publish external events (e.g. update Confluence page title)
					handleMarkdownStreaming(view, state, markdownStreamingState);

					// Detect finalised just completed, and auto accept AI changes is enabled, then we can auto accept the changes
					const justFinalised = state.hasFinalStream && !prevState?.hasFinalStream;
					if (justFinalised && canAutoAcceptAIChanges) {
						api?.aiExperience?.actions.endAIExperience();
						// Note: endAIExperience dispatches a transaction, which will trigger another update() call
						// In that subsequent call, currentSessionId will be cleared and isActive will be false
						return;
					}

					if (!api) {
						return;
					}

					handleLoadingAnimation(view, state, viewState);
					handleExpandNodes(view, state, prevState, api);
					handleEditorState(view, isActive, api, viewState, finalizeState?.isFinalizingInProgress);
				},

				destroy() {
					cancelLoadingAnimation(viewState);
					restoreEditorState(editorView, viewState);
					if (lastRenderedAgentKey !== null) {
						portalProviderAPI.remove(TELEPOINTER_AVATAR_PORTAL_KEY);
					}
				},
			};
		},
	});
};
